import moment from 'moment/moment'
import { useCallback, useMemo, useState } from 'react'

import { Dashboard } from '../../models/Dashboard'
import { Lesson } from '../../models/Lesson'
import { PAYEE_TYPE } from '../../models/Payee'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { DOW_DATE_LONG_FORMAT } from '../../utils/dates'
import { generateRandomID } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { Icon, IconName } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../app/design/Table'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { MoreMenuButton } from '../common/MoreMenuButton'
import { CapacityStringCell } from '../dashboards/ClassesDashboard'
import { EmptyDashboardLayout } from '../dashboards/EmptyDashboardLayout'
import { EditLessonScheduleModal } from '../items/EditLessonScheduleModal'
import { NEW_LESSON_ID_PREFIX } from '../items/ClassPage'
import { SettingsLayout } from './SettingsLayout'

const viewWillAppear = async (base: PocketComponent<void>) => {
    const id = base.urlParams.id as string
    const itemResult = await RecordFactory.getInstance().fetchItem(id, 'class')
    if (!itemResult.ok) {
        return new RecordNotFoundError()
    }

    await RecordFactory.getInstance().fetchCalendar(
        moment().add(-1, 'weeks'),
        moment().add(6, 'months'),
        undefined,
        id,
        undefined
    )

    const [item, lessons] = await Promise.all([
        RecordFactory.getInstance().loadItem(id),
        RecordFactory.getInstance().loadClasses(),
    ])
    if (!item) {
        return new RecordNotFoundError()
    }

    return {
        item,
        lessons: lessons.filter((l) => l.item?.id === id),
    }
}

function LessonMoreMenu({
    lesson,
    onEdit,
    onDelete,
}: {
    lesson: Lesson
    onEdit: (type: 'single' | 'series') => void
    onDelete: VoidFunction
}) {
    const [showDeleteModal, setShowDeleteModal] = useState(false)

    const options = useMemo(() => {
        const o: { id: string; title: string; icon: IconName; isDanger?: boolean }[] = [
            {
                id: 'edit',
                title: 'Edit single lesson',
                icon: 'calendar' as IconName,
            },
        ]

        if (lesson.isRecurring) {
            o.push({
                id: 'edit-series',
                title: 'Edit entire series',
                icon: 'calendars' as IconName,
            })
        }

        o.push({
            id: 'delete',
            title: 'Delete class lesson',
            icon: 'trash' as IconName,
            isDanger: true,
        })

        return o
    }, [])
    return (
        <>
            <div className="flex justify-end">
                <MoreMenuButton
                    options={options}
                    onSelectOption={(option) => {
                        switch (option.id) {
                            case 'delete':
                                setShowDeleteModal(true)
                                break
                            case 'edit':
                                onEdit('single')
                                break
                            case 'edit-series':
                                onEdit('series')
                                break
                        }
                    }}
                />
            </div>
            <ConfirmationModal
                title="Delete lesson"
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={() => {
                    setShowDeleteModal(false)
                    onDelete()
                }}
                isDestructive
            >
                <Paragraph>Are you sure you want to delete this lesson?</Paragraph>
            </ConfirmationModal>
        </>
    )
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const [lessons, setLessons] = useState<Lesson[]>(data.lessons)
    const [lessonToEdit, setLessonToEdit] = useState<
        { type: 'single' | 'series' | 'new'; lesson: Lesson } | undefined
    >()

    const { findLastHistoryEntry } = useHistoryTrackerContext()
    const backHistoryEntry = findLastHistoryEntry()

    const handleDelete = async (lessonToDelete: Lesson) => {
        const originalLessons = lessons
        const lessonsWithoutDeleted = lessons.filter((l) => l.id !== lessonToDelete.id)

        setLessons(lessonsWithoutDeleted)

        const clonedClass = data.item.clone()
        clonedClass.lessons = lessonsWithoutDeleted
        const result = await RecordFactory.getInstance().sendItem(clonedClass)
        if (!result.ok) {
            base.logAndDisplayError(result.error)
            setLessons(originalLessons)
        }
    }

    const handleSaveLesson = useCallback(
        async (editedLesson: Lesson, type: 'single' | 'series' | 'new', shouldNotify: boolean) => {
            setLessonToEdit(undefined)
            setLessons((lessons) => {
                // if the lesson is new, add it to the list
                if (!lessons.find((l) => l.id === editedLesson.id)) {
                    return [...lessons, editedLesson]
                }

                // if the lesson is not new, update the list
                return lessons.map((l) => {
                    if (l.id === editedLesson.id) {
                        return editedLesson.clone()
                    }

                    return l
                })
            })

            if (type === 'new') {
                editedLesson.id = undefined
            }

            const result = await RecordFactory.getInstance().sendClass(
                editedLesson,
                editedLesson.schedule,
                data.item,
                shouldNotify,
                type === 'series'
            )
            if (!result.ok) {
                base.logAndDisplayError(result.error)
            }
            else {
                base.reload()
            }
        },
        [base, lessonToEdit]
    )

    return (
        <>
            <SettingsLayout
                title={`Lessons for ${data.item.name}`}
                backButton={
                    backHistoryEntry
                        ? {
                              label: backHistoryEntry.title ?? 'Back',
                              href: backHistoryEntry.pathname,
                          }
                        : {
                              label: `Classes`,
                              href: `/settings/classes`,
                          }
                }
                actionButton={{
                    label: `Add a lesson`,
                    onClick: () =>
                        setLessonToEdit({
                            type: 'new',
                            lesson: new Lesson({
                                id: `${NEW_LESSON_ID_PREFIX}${generateRandomID()}`,
                                capacity: data.item.capacity,
                            }),
                        }),
                    icon: 'plus.small',
                    intent: 'primary',
                }}
            >
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {lessons.map((lesson) => (
                            <TableRow key={lesson.id}>
                                <TableCell>
                                    <div className="flex items-center gap-x-4">
                                        <Icon icon="student.medium" background="round" />
                                        <div className="flex flex-col gap-y-1">
                                            <Label size="sm">
                                                {lesson.date?.format(DOW_DATE_LONG_FORMAT)}
                                            </Label>
                                            <CapacityStringCell lesson={lesson} />
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <LessonMoreMenu
                                        lesson={lesson}
                                        onEdit={(type) => setLessonToEdit({ type, lesson })}
                                        onDelete={() => handleDelete(lesson)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                        {lessons.length === 0 && (
                            <TableCell colSpan={10}>
                                <EmptyDashboardLayout
                                    dashboard={
                                        new Dashboard({
                                            title: `No lessons for ${data.item.name}`,
                                        })
                                    }
                                    searchtext={''}
                                />
                            </TableCell>
                        )}
                    </TableBody>
                </Table>
            </SettingsLayout>
            {lessonToEdit && (
                <EditLessonScheduleModal
                    isOpen
                    onClose={() => setLessonToEdit(undefined)}
                    lesson={lessonToEdit.lesson}
                    count={
                        lessonToEdit.type === 'series'
                            ? lessons.filter(
                                  (l) => l.schedule?.id === lessonToEdit.lesson.schedule?.id
                              ).length
                            : undefined
                    }
                    type={lessonToEdit.type}
                    onDone={async (lesson, type, shouldNotify) => {
                        await handleSaveLesson(lesson, type, shouldNotify)
                    }}
                />
            )}
        </>
    )
}

export const SetupLessonsPage = usePSComponent(render, viewWillAppear)
